import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { 
  useGetTemplatesQuery,
  useDeleteTemplateMutation,
  useSubmitTemplateMutation,
  useCheckTemplateStatusQuery
} from '../store/services/whatsappApi';

// Helper function to check if a template name follows WhatsApp requirements
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isValidWhatsappTemplateName = (name: string): boolean => {
  return /^[a-z][a-z0-9_]*$/.test(name);
};

// Helper function to format a template name according to WhatsApp requirements
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formatWhatsappTemplateName = (name: string): string => {
  // Convert to lowercase
  let formatted = name.toLowerCase();
  
  // Replace any character that is not a lowercase letter, number or underscore
  formatted = formatted.replace(/[^a-z0-9_]/g, '_');
  
  // Ensure name starts with a letter
  if (!formatted || !(/^[a-z]/).test(formatted)) {
    formatted = 'template_' + formatted;
  }
  
  // Remove consecutive underscores
  formatted = formatted.replace(/_+/g, '_');
  
  // Remove trailing underscores
  formatted = formatted.replace(/_+$/g, '');
  
  return formatted;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type TemplateFormData = {
  name: string;
  content: string;
  category: string;
  variables?: string;
  variableExamples?: { [key: string]: string };
  type?: string; // text, media, interactive
  header_format?: string; // TEXT, IMAGE, VIDEO, DOCUMENT, LOCATION
  header_text?: string; // For TEXT headers
  media_handle?: string; // For IMAGE, VIDEO, DOCUMENT headers
};

const Templates: React.FC = () => {
  const navigate = useNavigate();
  const [previewTemplate, setPreviewTemplate] = useState<any>(null);
  const [statusCheckId, setStatusCheckId] = useState<number | null>(null);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<number | null>(null);
  
  // State for submission confirmation modal
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [templateToSubmit, setTemplateToSubmit] = useState<any>(null);
  const [submissionMessage, setSubmissionMessage] = useState<string>('');

  const { data: templates, isLoading, isError } = useGetTemplatesQuery({});
  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteTemplateMutation();
  const [submitTemplate, { isLoading: isSubmitting }] = useSubmitTemplateMutation();
  const { data: templateStatus, isLoading: isStatusLoading } = useCheckTemplateStatusQuery(
    statusCheckId as number,
    { skip: statusCheckId === null }
  );

  // All template creation and editing functionality has been moved to the TemplateCreate page

  const handleEdit = (template: any) => {
    navigate(`/templates/edit/${template.id}`);
  };

  const handleDeleteTemplate = (id: number) => {
    setTemplateToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteTemplate = async () => {
    if (templateToDelete) {
      try {
        await deleteTemplate(templateToDelete);
        setIsDeleteModalOpen(false);
        setTemplateToDelete(null);
      } catch (error) {
        console.error('Failed to delete template:', error);
      }
    }
  };

  const cancelDeleteTemplate = () => {
    setIsDeleteModalOpen(false);
    setTemplateToDelete(null);
  };

  const handleSubmitTemplate = (template: any) => {
    // Open confirmation modal with template details
    setTemplateToSubmit(template);
    setSubmissionMessage('');
    setIsSubmitModalOpen(true);
  };
  
  const confirmTemplateSubmission = async () => {
    if (!templateToSubmit) return;
    
    try {
      const result = await submitTemplate(templateToSubmit.id).unwrap();
      setSubmissionMessage(`Template submitted successfully! Status: ${result.status}`);
      // Keep modal open to show success message
    } catch (error) {
      console.error('Failed to submit template:', error);
      setSubmissionMessage('Failed to submit template. Please try again.');
    }
  };
  
  const closeSubmitModal = () => {
    setIsSubmitModalOpen(false);
    setTemplateToSubmit(null);
    setSubmissionMessage('');
  };

  const handleCheckStatus = (id: number) => {
    // Find the template to check its current language code
    const template = templates?.find((t: any) => t.id === id);
    if (template) {
      // Store the original language code to compare after status check
      (window as any).originalLanguageCode = template.language_code;
    }
    setStatusCheckId(id);
    setIsStatusModalOpen(true);
  };

  const closeStatusModal = () => {
    setIsStatusModalOpen(false);
    setStatusCheckId(null);
  };

  const handlePreview = (template: any) => {
    setPreviewTemplate(template);
  };

  const closePreview = () => {
    setPreviewTemplate(null);
  };

  const navigateToCreateTemplate = () => {
    navigate('/templates/create');
  };

  return (
    <MainLayout>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-whatsapp-teal">Message Templates</h1>
          <button
            onClick={navigateToCreateTemplate}
            className="bg-whatsapp-green hover:bg-whatsapp-dark-green text-white px-4 py-2 rounded-md flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Template
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-whatsapp-green"></div>
          </div>
        ) : isError ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p>Error loading templates. Please try again later.</p>
          </div>
        ) : templates?.length === 0 ? (
          <div className="bg-white shadow-md rounded-lg p-10 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h2 className="text-xl text-gray-600 mb-2">No templates yet</h2>
            <p className="text-gray-500 mb-4">Create your first WhatsApp message template to start sending messages at scale.</p>
            <button
              onClick={navigateToCreateTemplate}
              className="bg-whatsapp-green hover:bg-whatsapp-dark-green text-white px-4 py-2 rounded-md"
            >
              Create Template
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates?.map((template: any) => (
              <div key={template.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow flex flex-col">
                <div className="p-4 border-b border-gray-100">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                      <div className="flex flex-wrap gap-2 mt-1">
                        <span className="inline-block bg-gray-100 rounded-full px-3 py-1 text-xs font-semibold text-gray-600">
                          {template.category}
                        </span>
                        <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${template.status === 'APPROVED' ? 'bg-green-100 text-green-800' : 
                          template.status === 'REJECTED' ? 'bg-red-100 text-red-800' : 
                          template.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' : 
                          template.status === 'PAUSED' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                          {template.status}
                        </span>
                        {template.language_code && (
                          <span className="inline-block bg-blue-100 rounded-full px-3 py-1 text-xs font-semibold text-blue-800" title="Template Language">
                            🌐 {template.language_code}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex">
                      <button 
                        onClick={() => handlePreview(template)} 
                        className="text-gray-500 hover:text-whatsapp-green mr-2"
                        title="Preview"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button 
                        onClick={() => handleEdit(template)} 
                        className="text-gray-500 hover:text-blue-500 mr-2"
                        title="Edit"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button 
                        onClick={() => handleDeleteTemplate(template.id)} 
                        className="text-gray-500 hover:text-red-500"
                        title="Delete"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="p-4 flex-grow flex flex-col">
                  <p className="text-gray-600 text-sm whitespace-pre-line line-clamp-3">{template.content}</p>
                  {template.variables && template.variables.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500">Variables:</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {template.variables.map((variable: string, index: number) => (
                          <span key={index} className="inline-block bg-whatsapp-light-green rounded-full px-2 py-1 text-xs font-semibold text-whatsapp-dark-green">
                            {variable}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-auto pt-4 flex justify-between items-center border-t">
                    {template.status === 'DRAFT' && (
                      <button
                        onClick={() => handleSubmitTemplate(template)}
                        disabled={isSubmitting}
                        className="bg-whatsapp-green hover:bg-whatsapp-dark-green text-white text-xs px-3 py-1 rounded-md flex items-center"
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Submitting...
                          </>
                        ) : (
                          <>Submit for Approval</>
                        )}
                      </button>
                    )}
                    {template.status === 'REJECTED' && (
                      <button
                        onClick={() => handleSubmitTemplate(template)}
                        disabled={isSubmitting}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white text-xs px-3 py-1 rounded-md flex items-center"
                      >
                        {isSubmitting ? 'Resubmitting...' : 'Resubmit'}
                      </button>
                    )}
                    {template.status === 'PENDING' && (
                      <span className="text-yellow-600 text-xs">Awaiting approval</span>
                    )}
                    {template.status === 'APPROVED' && (
                      <span className="text-green-600 text-xs">Ready to use</span>
                    )}
                    <button
                      onClick={() => handleCheckStatus(template.id)}
                      className="text-gray-500 hover:text-blue-500 text-xs px-3 py-1 rounded-md border border-gray-300 hover:border-blue-500"
                    >
                      Check Status
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Template Preview Modal */}
        {previewTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-md">
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="text-lg font-medium text-gray-900">Template Preview</h2>
                <button onClick={closePreview} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-4 space-y-4">
                {previewTemplate.header_format === 'IMAGE' && (
                  <div className="bg-gray-100 p-3 rounded-lg text-center">
                    <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-gray-600 mt-2">Image Header</p>
                  </div>
                )}
                {previewTemplate.header_format === 'VIDEO' && (
                  <div className="bg-gray-100 p-3 rounded-lg text-center">
                    <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-gray-600 mt-2">Video Header</p>
                  </div>
                )}
                {previewTemplate.header_format === 'DOCUMENT' && (
                  <div className="bg-gray-100 p-3 rounded-lg text-center">
                    <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm text-gray-600 mt-2">Document Header</p>
                  </div>
                )}
                {previewTemplate.header_format === 'TEXT' && previewTemplate.header_text && (
                  <div className="bg-gray-100 p-3 rounded-lg">
                    <h3 className="font-semibold text-gray-800">{previewTemplate.header_text}</h3>
                  </div>
                )}
                <div className="whitespace-pre-wrap text-gray-700">
                  {previewTemplate.content}
                </div>
                {previewTemplate.variables && previewTemplate.variables.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-semibold mb-2">Variables:</p>
                    <div className="flex flex-wrap gap-1">
                      {previewTemplate.variables.map((v: string, i: number) => (
                        <span key={i} className="inline-block bg-blue-100 text-blue-800 rounded-full px-2 py-1 text-xs font-semibold">
                          {v}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {/* Template Status Modal */}
        {isStatusModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-md">
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="text-lg font-medium text-gray-900">Template Status</h2>
                <button onClick={closeStatusModal} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-4">
                {isStatusLoading ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-whatsapp-green"></div>
                  </div>
                ) : templateStatus ? (
                  <div>
                    <div className="mb-4">
                      <h3 className="text-sm font-bold text-gray-700 mb-1">Status</h3>
                      <p className={`text-lg font-semibold ${
                        templateStatus.status === 'APPROVED' ? 'text-green-600' : 
                        templateStatus.status === 'REJECTED' ? 'text-red-600' : 
                        templateStatus.status === 'PENDING' ? 'text-yellow-600' : 
                        'text-gray-600'
                      }`}>
                        {templateStatus.status}
                      </p>
                    </div>
                    {templateStatus.status === 'REJECTED' && templateStatus.rejected_reason && (
                      <div className="mb-4">
                        <h3 className="text-sm font-bold text-gray-700 mb-1">Rejection Reason</h3>
                        <p className="text-red-600">{templateStatus.rejected_reason}</p>
                      </div>
                    )}
                    <div className="mb-4">
                      <h3 className="text-sm font-bold text-gray-700 mb-1">Template ID</h3>
                      <p>{templateStatus.whatsapp_template_id || templateStatus.template_id}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-700 mb-1">Name</h3>
                      <p>{templateStatus.name}</p>
                    </div>
                    {templateStatus.language_code && (
                      <div className="mt-3">
                        <h3 className="text-sm font-bold text-gray-700 mb-1">Language Code</h3>
                        <p className="flex items-center">
                          <span className="inline-block bg-blue-100 rounded-full px-3 py-1 text-sm font-semibold text-blue-800">
                            🌐 {templateStatus.language_code}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          This template is approved for {templateStatus.language_code} language
                        </p>
                        {(window as any).originalLanguageCode && 
                         (window as any).originalLanguageCode !== templateStatus.language_code && (
                          <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <div className="flex items-start">
                              <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              <div className="text-sm text-yellow-800">
                                <p className="font-medium">Language Code Updated!</p>
                                <p className="mt-1">
                                  Changed from <code className="bg-yellow-100 px-1 rounded">{(window as any).originalLanguageCode}</code> to <code className="bg-yellow-100 px-1 rounded">{templateStatus.language_code}</code>
                                </p>
                                <p className="mt-1 text-xs">
                                  The database has been updated to match WhatsApp's approved language.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="mt-3">
                      <h3 className="text-sm font-bold text-gray-700 mb-1">WhatsApp Status</h3>
                      <p>{templateStatus.whatsapp_status}</p>
                    </div>
                    {templateStatus.submitted_at && (
                      <div className="mt-3">
                        <h3 className="text-sm font-bold text-gray-700 mb-1">Submitted At</h3>
                        <p>{new Date(templateStatus.submitted_at).toLocaleString()}</p>
                      </div>
                    )}
                    {templateStatus.approved_at && (
                      <div className="mt-3">
                        <h3 className="text-sm font-bold text-gray-700 mb-1">Approved At</h3>
                        <p>{new Date(templateStatus.approved_at).toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-red-600">Unable to retrieve template status information.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Template Delete Confirmation Modal */}
        {isDeleteModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-md">
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="text-lg font-medium text-gray-900">Confirm Delete</h2>
                <button onClick={cancelDeleteTemplate} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-4">
                <p className="mb-4">Are you sure you want to delete this template? This action cannot be undone.</p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={cancelDeleteTemplate}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteTemplate}
                    disabled={isDeleting}
                    className={`bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-md ${isDeleting ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {isDeleting ? (
                      <div className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Deleting...
                      </div>
                    ) : 'Delete Template'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Template Submission Confirmation Modal */}
        {isSubmitModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-md">
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="text-lg font-medium text-gray-900">Submit Template</h2>
                <button onClick={closeSubmitModal} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-4">
                {submissionMessage ? (
                  <div className={submissionMessage.includes('successfully') ? 'text-green-600' : 'text-red-600'}>
                    {submissionMessage}
                  </div>
                ) : (
                  <>
                    <p className="mb-4">
                      Are you sure you want to submit this template for WhatsApp approval? Once submitted, the template will be reviewed by WhatsApp, which usually takes 1-2 business days.
                    </p>
                    <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-md text-sm">
                      <p className="font-semibold mb-1">Important Notes:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Templates must comply with WhatsApp's Business Policy</li>
                        <li>All variable examples must be appropriate and realistic</li>
                        <li>Avoid using all caps, excessive punctuation or emojis</li>
                        <li>Template category should match the content type</li>
                      </ul>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={closeSubmitModal}
                        className="bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-md"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={confirmTemplateSubmission}
                        disabled={isSubmitting}
                        className={`bg-whatsapp-green hover:bg-whatsapp-dark-green text-white py-2 px-4 rounded-md ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        {isSubmitting ? (
                          <div className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Submitting...
                          </div>
                        ) : 'Submit for Approval'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Templates;
